import React, { useState } from "react";
import {
    FaArrowRight, FaCheckCircle, FaChevronDown, FaChevronUp,
    FaChartLine, FaUsers, FaFileAlt, FaCamera, FaExclamationTriangle, FaCar,
    FaMapMarkerAlt, FaLock, FaCloud, FaFingerprint, FaBarcode, FaEye,
    FaBalanceScale, FaGlobe, FaSyncAlt, FaFileContract, FaUserFriends,
} from "react-icons/fa";
import HeaderAny from "../../components/header";
import MobileNav from "../../components/header-mobile";
import Footer from "../../components/footer";
import buildingImg from "../../assets/bnk-hero-skyline.jpg";
import "./solutions.css";
import "./banks.css";
import verificationDiagram from "../../assets/pub-verification-diagram.svg";

const GET_STARTED_URL = "https://web.conra.net";

const HERO_STATS = [
    { icon: FaLock, label: "Bank-Grade Security" },
    { icon: FaFingerprint, label: "C2PA Verified Media" },
    { icon: FaEye, label: "Real-Time Visibility" },
];

const PROBLEMS = [
    { Icon: FaCamera, title: "Outdated Photos", desc: "Being sent old progress pictures or images from entirely different projects." },
    { Icon: FaExclamationTriangle, title: "Edited Media", desc: "Digital alterations designed to make a project look more advanced than it is." },
    { Icon: FaCar, title: "Expensive Site Visits", desc: "Sending physical inspectors to remote sites slows down loan disbursements and increases overhead." },
];

const SOLUTION_FEATURES = [
    { title: "Instantly locked at capture", desc: "No delay between the shutter click and the tamper-proof seal." },
    { title: "Location and time verified", desc: "Every capture is geo-tagged and time-stamped against the project site." },
    { title: "Impossible to fake", desc: "C2PA-secured records make undetected edits or substitutions impossible." },
];

const WHEEL_ITEMS = [
    { Icon: FaMapMarkerAlt, label: "Geo-Tagged\nMedia", angle: 270 },
    { Icon: FaFingerprint, label: "Content Authenticity\n(C2PA)", angle: 330 },
    { Icon: FaChartLine, label: "Time & Location\nStamped", angle: 30 },
    { Icon: FaBarcode, label: "Company Unique\nCode (KN21G)", angle: 90 },
    { Icon: FaFileAlt, label: "Immutable Audit\nTrail", angle: 150 },
    { Icon: FaCloud, label: "Secure Cloud\nStorage", angle: 210 },
];

const LENDER_STEPS = [
    { num: "1", title: "Set your milestones", desc: "Define the construction phases in ConRa that need visual proof before you release the next part of the loan." },
    { num: "2", title: "On-site capture", desc: "The contractor on the ground uses the ConRa app to take photos of the completed work. The app instantly seals the photo with tamper-proof security metadata (C2PA standard) at the exact moment of capture." },
    { num: "3", title: "Instant verification", desc: "Our system automatically runs a check to ensure the photo is genuine, taken at the correct GPS coordinates, and hasn't been edited or uploaded from a camera roll." },
    { num: "4", title: "Confidently release funds", desc: "Your loan officers review the verified visual portfolio right from their desks and confidently approve the next phase of funding." },
];

const TRUST_REASONS = [
    { Icon: FaCheckCircle, title: "Zero Guesswork", desc: "Eliminate the risk of paying for incomplete work or “ghost” milestones." },
    { Icon: FaFileAlt, title: "Ironclad Audit Trails", desc: "Maintain a secure, chronological timeline of the entire build from foundation to finish for audit compliance and board reviews." },
    { Icon: FaChartLine, title: "Faster Turnaround Times", desc: "Stop waiting long for physical site inspectors. Approve progress updates in hours, keeping your projects moving smoothly." },
    { Icon: FaUsers, title: "Stronger Developer Trust", desc: "Keep projects on schedule by keeping capital flowing quickly and predictably." },
];

const TEAM_CARDS = [
    { Icon: FaBalanceScale, tag: "BUILDS TRUST", title: "Credit & Risk Team", desc: "Links each funding request to visible progress so decisions are not based on scattered photos or verbal updates." },
    { Icon: FaGlobe, tag: "PROTECTS CAPITAL", title: "Diaspora Banking Team", desc: "Gives diaspora clients confidence that their construction money is being monitored locally through a trusted bank channel." },
    { Icon: FaSyncAlt, tag: "SAVES TIME", title: "Operations Team", desc: "Reduces repeated follow-ups, manual photo collection and unstructured email or WhatsApp evidence." },
    { Icon: FaFileContract, tag: "AUDIT TRAIL", title: "Compliance & Internal Audit", desc: "Creates an organized trail of project evidence, approvals, stakeholders and milestone history — a verified record inside your own systems, ready for any review.", highlighted: true },
];

const RELATIONSHIP_CARD = { Icon: FaUserFriends, tag: "GROWS REVENUE", title: "Relationship Managers", desc: "Creates opportunities for cross-sell — accounts, insurance, mortgage takeout, savings — and long-term client retention." };

/* Spoke diagram – pure SVG */
const WheelDiagram = () => {
    const cx = 160; const cy = 160; const r = 110; const dotR = 6;
    const toRad = (deg) => (deg * Math.PI) / 180;
    return (
        <svg viewBox="0 0 320 320" width="320" height="320" className="bnk-wheel-svg">
            {/* spokes */}
            {WHEEL_ITEMS.map(({ angle }) => {
                const x = cx + r * Math.cos(toRad(angle));
                const y = cy + r * Math.sin(toRad(angle));
                return <line key={angle} x1={cx} y1={cy} x2={x} y2={y} stroke="#dbeafe" strokeWidth="1.5" />;
            })}
            {/* outer ring */}
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#dbeafe" strokeWidth="1" />
            {/* center */}
            <circle cx={cx} cy={cy} r={40} fill="var(--color-primary)" />
            <text x={cx} y={cy - 6} textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">ConRa</text>
            <text x={cx} y={cy + 10} textAnchor="middle" fontSize="10" fill="rgba(255,255,255,0.75)">Platform</text>
            {/* nodes */}
            {WHEEL_ITEMS.map(({ Icon: _I, label, angle }) => {
                const x = cx + r * Math.cos(toRad(angle));
                const y = cy + r * Math.sin(toRad(angle));
                const lines = label.split("\n");
                const isRight = x > cx + 20;
                const isLeft = x < cx - 20;
                const anchor = isRight ? "start" : isLeft ? "end" : "middle";
                const lx = isRight ? x + 12 : isLeft ? x - 12 : x;
                return (
                    <g key={angle}>
                        <circle cx={x} cy={y} r={dotR + 4} fill="#eff6ff" />
                        <circle cx={x} cy={y} r={dotR} fill="var(--color-primary)" />
                        {lines.map((line, i) => (
                            <text
                                key={i}
                                x={lx}
                                y={y + (i - (lines.length - 1) / 2) * 14}
                                textAnchor={anchor}
                                fontSize="10"
                                fontWeight="600"
                                fill="#374151"
                                dominantBaseline="middle"
                            >{line}</text>
                        ))}
                    </g>
                );
            })}
        </svg>
    );
};

const BanksPage = () => {
    const [openFeat, setOpenFeat] = useState(0);

    return (
        <div>
            <div className="web-only"><HeaderAny /></div>
            <div className="mobile-only"><MobileNav /></div>

            {/* ── Hero ── */}
            <section className="bnk-hero">
                <div className="bnk-hero-inner">
                    <div className="bnk-hero-text">
                        <p className="bnk-hero-overtitle">Solutions for Banks &amp; Lenders</p>
                        <h1 className="bnk-hero-h1">
                            Construction Financing,<br />Visualized and Verified
                        </h1>
                        <p className="bnk-hero-sub">
                            Bridge the trust gap between your loan department and the physical construction site.
                            Release funds with absolute confidence using tamper-proof visual progress tracking.
                        </p>
                        <div className="bnk-hero-actions">
                            <button className="filled-button" onClick={() => window.open(GET_STARTED_URL, "_blank")}>
                                Talk to Sales <FaArrowRight style={{ marginLeft: 6 }} />
                            </button>
                        </div>
                        <div className="bnk-hero-stats">
                            {HERO_STATS.map(({ icon: Icon, label }) => (
                                <span key={label} className="bnk-hero-stat">
                                    <Icon className="bnk-hero-stat-icon" />
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="bnk-hero-visual">
                        <img src={buildingImg} alt="Financial district skyline" className="bnk-hero-img" />
                    </div>
                </div>
            </section>

            {/* ── Problem ── */}
            <section className="bnk-problem-section">
                <div className="bnk-problem-card">
                    <p className="bnk-problem-overtitle">THE PROBLEM</p>
                    <h2 className="bnk-problem-h2">The Lending Blind Spot</h2>
                    <p className="bnk-problem-sub">
                        When funding a construction project, you release capital in stages based on physical milestones
                        at the construction site. We know that relying on standard photos or unverified paper reports
                        introduces significant risk.
                    </p>
                    <div className="bnk-problem-grid">
                        {PROBLEMS.map(({ Icon, title, desc }) => (
                            <div key={title} className="bnk-problem-item">
                                <span className="bnk-problem-icon"><Icon /></span>
                                <h4 className="bnk-problem-item-title">{title}</h4>
                                <p className="bnk-problem-item-desc">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Solution ── */}
            <section className="bnk-solution">
                <div className="bnk-solution-inner">
                    <div className="bnk-solution-text">
                        <p className="bnk-solution-label">The ConRa Solution</p>
                        <h2 className="bnk-solution-h2">See the Truth in Real Time</h2>
                        <p className="bnk-solution-body">
                            ConRa bridges the gap between your loan department and the physical construction site. We
                            provide a secure platform where builders capture progress photos that are instantly locked,
                            verified, and impossible to fake.
                        </p>
                        <p className="bnk-solution-body">
                            With ConRa, every photo in your project portfolio comes with a digital guarantee that it was
                            taken at the exact location, at the exact time, and has not been altered in any way.
                        </p>
                        <div className="bnk-features-accordion">
                            {SOLUTION_FEATURES.map(({ title, desc }, i) => (
                                <div key={title} className={`bnk-feat-item ${openFeat === i ? "is-open" : ""}`}>
                                    <button className="bnk-feat-trigger" onClick={() => setOpenFeat(openFeat === i ? null : i)}>
                                        <FaCheckCircle className="bnk-feat-check" />
                                        <span>{title}</span>
                                        {openFeat === i ? <FaChevronUp className="bnk-feat-chevron" /> : <FaChevronDown className="bnk-feat-chevron" />}
                                    </button>
                                    {openFeat === i && <p className="bnk-feat-desc">{desc}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="pub-verification-right">
                        <img
                            src={verificationDiagram}
                            alt="ConRa Verification Engine diagram"
                            className="pub-ver-diagram-img"
                        />
                    </div>
                </div>
            </section>

            {/* ── How It Works ── */}
            <section className="bnk-steps-section">
                <div className="bnk-steps-card">
                    <h2 className="bnk-steps-h2">How It Works for Lenders</h2>
                    <div className="bnk-steps-list">
                        {LENDER_STEPS.map(({ num, title, desc }) => (
                            <div key={num} className="bnk-step">
                                <div className="bnk-step-num">{num}</div>
                                <div>
                                    <h4 className="bnk-step-title">{title}</h4>
                                    <p className="bnk-step-tag">Step {num}</p>
                                    <p className="bnk-step-desc">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Trust ── */}
            <section className="bnk-trust">
                <div className="bnk-trust-inner">
                    <h2 className="bnk-trust-h2">Why Financial Institutions Trust ConRa</h2>
                    <div className="bnk-trust-grid">
                        {TRUST_REASONS.map(({ Icon, title, desc }) => (
                            <div key={title} className="bnk-trust-card">
                                <span className="bnk-trust-icon"><Icon /></span>
                                <h4 className="bnk-trust-title">{title}</h4>
                                <p className="bnk-trust-desc">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Bank Teams ── */}
            <section className="bnk-teams">
                <div className="bnk-teams-inner">
                    <p className="bnk-teams-overtitle">BANK TEAM BENEFITS</p>
                    <h2 className="bnk-teams-h2">Where ConRa Helps Each Bank Team</h2>
                    <p className="bnk-teams-sub">
                        ConRa turns construction financing into a monitored relationship, not a one-time loan
                        transaction — every team gets visibility built for the way they work.
                    </p>

                    {/* Row 1: 4 equal cards */}
                    <div className="bnk-teams-row4">
                        {TEAM_CARDS.map(({ Icon, tag, title, desc, highlighted }) => (
                            <div key={title} className={`bnk-team-card ${highlighted ? "bnk-team-highlighted" : ""}`}>
                                <p className="bnk-team-tag">{tag}</p>
                                <span className="bnk-team-icon"><Icon /></span>
                                <h4 className="bnk-team-title">{title}</h4>
                                <p className="bnk-team-desc">{desc}</p>
                                {highlighted && (
                                    <span className="bnk-team-badge">
                                        <FaCheckCircle style={{ marginRight: 6 }} /> ConRa Verified Evidence
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Row 2: centered */}
                    <div className="bnk-teams-row-center">
                        <div className="bnk-team-card">
                            <p className="bnk-team-tag bnk-team-tag-amber">{RELATIONSHIP_CARD.tag}</p>
                            <span className="bnk-team-icon"><RELATIONSHIP_CARD.Icon /></span>
                            <h4 className="bnk-team-title">{RELATIONSHIP_CARD.title}</h4>
                            <p className="bnk-team-desc">{RELATIONSHIP_CARD.desc}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="bnk-cta">
                <div className="bnk-cta-overlay" />
                <div className="bnk-cta-inner">
                    <h2 className="bnk-cta-h2">Ready to De-Risk Your Loan Portfolio?</h2>
                    <p className="bnk-cta-sub">
                        Join lenders and financial institutions using ConRa to verify every dollar of
                        construction financing before it's released.
                    </p>
                    <div className="bnk-cta-actions">
                        <button className="filled-button" onClick={() => window.open(GET_STARTED_URL, "_blank")}>
                            Talk to Sales
                        </button>
                        <button className="bnk-cta-outlined" onClick={() => window.open(GET_STARTED_URL, "_blank")}>
                            See How It Works
                        </button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default BanksPage;
